// Private component for the internal scrollbar rendering DOM
// We wrap this in its own component so we can localize its updates

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import cx from 'classnames'
import Timing from '../../hoc/Timing'

const THUMB_WIDTH = 10
const SCROLL_SHADY_TRANSLATE_OFFSET = 10 // px offset that scroll shadows will use when translating in/out
const SCROLL_SHADY_FADE_MIN_PCT = 0.5 // percent of scroll travel over which shadows will fade in, up to the max dist
const SCROLL_SHADY_FADE_MAX_DIST = 500 // max distance from edges of scroll over which shadows will fade in

class ScrollImpl extends React.Component {
  static displayName = 'ScrollBars_Impl'

  static propTypes = {
    className: T.string,
    horizontal: T.bool,
    onThumbScroll: T.func,
    onWheel: T.func,
    slimShady: T.bool,
    slimShadyDarker: T.bool,
    thumbMinLength: T.number,
    vertical: T.bool,
    setTimer: T.func.isRequired,
    clearTimer: T.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      clientWidth: 0,
      clientHeight: 0,
      scrollWidth: 0,
      scrollHeight: 0,
      scrollLeft: 0,
      scrollTop: 0,
      dragging: false,
      updatedRecently: false
    }
  }

  componentWillUnmount() {
    this._isUnmounting = true
    this._endDrag()
  }

  // Sidestep React's validation that prevents calling setProps/setState from the parent component. Normally
  // that validation makes sense but this is a special case where we really do want to update only the
  // sub-component without triggering a re-render of the entire content subtree.
  setStatePlzThx = (state, callback) => {
    this.setState(_.assign({ updatedRecently: true }, state), callback)
    this.props.setTimer(this._clearUpdatedRecently, 1000, 'recency')
  }

  _clearUpdatedRecently = () => {
    this.setState({ updatedRecently: false })
  }

  _onVThumbMouseDown = e => {
    return this._startDrag(e, 'clientY', 'Top')
  }

  _onHThumbMouseDown = e => {
    return this._startDrag(e, 'clientX', 'Left')
  }

  _startDrag = (e, mouseProp, position) => {
    e.preventDefault()
    this._dragInfo = {
      mouseStart: this._getMousePropValue(e, mouseProp),
      position: position,
      startMetrics: this._getThumbMetrics()
    }
    this.setState({ dragging: true })
    window.addEventListener('mousemove', this._onDrag)
    window.addEventListener('touchmove', this._onDrag)
    window.addEventListener('mouseup', this._endDrag)
    window.addEventListener('touchend', this._endDrag)
  }

  _getMousePropValue = (e, mouseProp) => {
    return e.type.indexOf('touch') !== -1
      ? e.touches[0][mouseProp]
      : e[mouseProp]
  }

  _onDrag = e => {
    const state = this.state
    const { position, mouseStart, startMetrics } = this._dragInfo
    const {
      vThumbHeight,
      vThumbY,
      hThumbWidth,
      hThumbX,
      vTrackLength,
      hTrackLength
    } = startMetrics

    var scrollLeft, scrollTop
    if (position === 'Top') {
      const newThumbY =
        vThumbY + (this._getMousePropValue(e, 'clientY') - mouseStart)
      const percent = newThumbY / (vTrackLength - vThumbHeight)
      scrollTop = (state.scrollHeight - state.clientHeight) * percent
      scrollLeft = state.scrollLeft
    } else {
      const newThumbX =
        hThumbX + (this._getMousePropValue(e, 'clientX') - mouseStart)
      const percent = newThumbX / (hTrackLength - hThumbWidth)
      scrollLeft = (state.scrollWidth - state.clientWidth) * percent
      scrollTop = state.scrollTop
    }
    return this.props.onThumbScroll(scrollLeft, scrollTop)
  }

  _endDrag = () => {
    delete this._dragInfo
    if (!this._isUnmounting) {
      this.setState({ dragging: false })
    }
    window.removeEventListener('mousemove', this._onDrag)
    window.removeEventListener('touchmove', this._onDrag)
    window.removeEventListener('mouseup', this._endDrag)
    window.removeEventListener('touchend', this._endDrag)
  }

  _getThumbMetrics = () => {
    const { horizontal, vertical, thumbMinLength } = this.props
    const {
      clientHeight,
      clientWidth,
      scrollHeight,
      scrollWidth,
      scrollTop,
      scrollLeft
    } = this.state
    const isScrollingV = vertical && scrollHeight > clientHeight
    const isScrollingH = horizontal && scrollWidth > clientWidth
    const vTrackLength = clientHeight - (isScrollingH ? THUMB_WIDTH : 0)
    const hTrackLength = clientWidth - (isScrollingV ? THUMB_WIDTH : 0)
    const vThumbHeight = isScrollingV
      ? Math.max(clientHeight * clientHeight / scrollHeight, thumbMinLength)
      : 0
    const hThumbWidth = isScrollingH
      ? Math.max(clientWidth * clientWidth / scrollWidth, thumbMinLength)
      : 0
    const vThumbY = isScrollingV
      ? (vTrackLength - vThumbHeight) *
        (scrollTop / (scrollHeight - clientHeight))
      : 0
    const hThumbX = isScrollingH
      ? (hTrackLength - hThumbWidth) *
        (scrollLeft / (scrollWidth - clientWidth))
      : 0
    return {
      isScrollingV,
      isScrollingH,
      vThumbHeight,
      vThumbY,
      hThumbWidth,
      hThumbX,
      vTrackLength,
      hTrackLength
    }
  }

  render() {
    const { className, slimShady, slimShadyDarker, onWheel } = this.props
    const {
      clientHeight,
      clientWidth,
      dragging,
      scrollHeight,
      //scrollLeft,
      scrollTop,
      scrollWidth,
      updatedRecently
    } = this.state
    const {
      hThumbWidth,
      hThumbX,
      isScrollingH,
      isScrollingV,
      vThumbHeight,
      vThumbY
    } = this._getThumbMetrics()

    let topFadePct = 0
    let bottomFadePct = 0
    const leftFadePct = 0
    const rightFadePct = 0
    if (isScrollingV && slimShady) {
      // fade in over 50% of total scroll height, up to a max distance
      const fadeDist = Math.min(SCROLL_SHADY_FADE_MAX_DIST, (scrollHeight - clientHeight) * SCROLL_SHADY_FADE_MIN_PCT)
      topFadePct = Math.min(1, scrollTop / fadeDist)
      bottomFadePct = Math.min(1, (scrollHeight - scrollTop - clientHeight) / fadeDist)
    }
    if (isScrollingH && slimShady) {
      // fade in over 50% of total scroll width, up to a max distance
      const fadeDist = Math.min(SCROLL_SHADY_FADE_MAX_DIST, (scrollWidth - clientWidth) * SCROLL_SHADY_FADE_MIN_PCT)
      topFadePct = Math.min(1, scrollTop / fadeDist)
      bottomFadePct = Math.min(1, (scrollHeight - scrollTop - clientHeight) / fadeDist)
    }

    return (
      <div
        className={cx(
          'pw_scroll_impl',
          className || '',
          dragging && 'dragging',
          updatedRecently && 'updated_recently'
        )}
        onWheel={onWheel}
      >
        {slimShady
          ? <div className="scroll_shadow_wrap">
              {isScrollingV
                ? <div
                    className={`scroll_shadow ${slimShadyDarker
                      ? 'darker'
                      : ''} scroll_shadow_v scroll_shadow_upper`}
                    style={{
                      opacity: topFadePct,
                      WebkitTransform: `translateY(-${(1 - topFadePct) * SCROLL_SHADY_TRANSLATE_OFFSET}px)`,
                      transform: `translateY(-${(1 - topFadePct) * SCROLL_SHADY_TRANSLATE_OFFSET}px)`
                    }}
                  />
                : null}
              {isScrollingV
                ? <div
                    className={`scroll_shadow ${slimShadyDarker
                      ? 'darker'
                      : ''} scroll_shadow_v scroll_shadow_lower`}
                    style={{
                      opacity: bottomFadePct,
                      WebkitTransform: `translateY(${(1 - bottomFadePct) * SCROLL_SHADY_TRANSLATE_OFFSET}px)`,
                      transform: `translateY(${(1 - bottomFadePct) * SCROLL_SHADY_TRANSLATE_OFFSET}px)`
                    }}
                  />
                : null}
              {isScrollingH
                ? <div
                    className={`scroll_shadow ${slimShadyDarker
                      ? 'darker'
                      : ''} scroll_shadow_h scroll_shadow_left`}
                    style={{
                      opacity: leftFadePct,
                      WebkitTransform: `translateX(${(1 - leftFadePct) * SCROLL_SHADY_TRANSLATE_OFFSET}px)`,
                      transform: `translateX(-${(1 - leftFadePct) * SCROLL_SHADY_TRANSLATE_OFFSET}px)`
                    }}
                  />
                : null}
              {isScrollingH
                ? <div
                    className={`scroll_shadow ${slimShadyDarker
                      ? 'darker'
                      : ''} scroll_shadow_h scroll_shadow_right`}
                    style={{
                      opacity: rightFadePct,
                      WebkitTransform: `translateX(${(1 - rightFadePct) * SCROLL_SHADY_TRANSLATE_OFFSET}px)`,
                      transform: `translateX(${(1 - rightFadePct) * SCROLL_SHADY_TRANSLATE_OFFSET}px)`
                    }}
                  />
                : null}
            </div>
          : null}
        {isScrollingV
          ? <div
              className="pw_scroll_thumb vertical"
              onMouseDown={this._onVThumbMouseDown}
              onTouchStart={this._onVThumbMouseDown}
              style={{
                transform: `translateY(${vThumbY}px)`,
                WebkitTransform: `translateY(${vThumbY}px)`,
                bottom: clientHeight - vThumbHeight
              }}
            />
          : null}
        {isScrollingH
          ? <div
              className="pw_scroll_thumb horizontal"
              onMouseDown={this._onHThumbMouseDown}
              onTouchStart={this._onHThumbMouseDown}
              style={{
                transform: `translateX(${hThumbX}px)`,
                WebkitTransform: `translateX(${hThumbX}px)`,
                right: clientWidth - hThumbWidth
              }}
            />
          : null}
      </div>
    )
  }
}

export default Timing(ScrollImpl)
